import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Link2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { historicalConversionService } from '../../services';
import { SourceSummary } from './SourceSummary';
import { HousePicker } from './HousePicker';

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : d?.message || fallback;
};

export const PsbDecodeBox = ({ onDecoded }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [houses, setHouses] = useState(null);
  const [error, setError] = useState(null);
  const decode = async (house) => {
    setBusy(true);
    setError(null);
    try {
      const r = await historicalConversionService.uploadSource(file, undefined, house);
      setHouses(null);
      onDecoded(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'multiple_houses') setHouses(d.houses);
      else setError(errText(e, 'Could not decode the file'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="psb-decode-box">
      <Input type="file" accept=".psb,.PSB" onChange={(e) => { setFile(e.target.files?.[0] || null); setHouses(null); setError(null); }} data-testid="psb-decode-file-input" />
      {houses && <HousePicker houses={houses} onPick={decode} busy={busy} />}
      {error && <Alert variant="destructive" data-testid="psb-decode-error"><AlertDescription>{error}</AlertDescription></Alert>}
      {!houses && (
        <Button disabled={!file || busy} onClick={() => decode()} data-testid="psb-decode-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Read file
        </Button>
      )}
    </div>
  );
};

const FIELDS = [
  ['name', 'Site name', true], ['address_line_1', 'Address line 1'], ['address_line_2', 'Address line 2'],
  ['city', 'Town / city', true], ['postcode', 'Postcode'], ['contact_name', 'Contact'],
];

const CreateSiteForm = ({ proposal, clients, onCreate, busy }) => {
  const [form, setForm] = useState({ ...proposal, client_id: '' });
  const missing = !form.client_id || !(form.name || '').trim() || !(form.city || '').trim();
  return (
    <div className="space-y-3 rounded-md border p-4" data-testid="site-profile-create-form">
      <div className="flex items-center gap-2 text-sm font-medium"><Building2 className="h-4 w-4" />Create a new site from this file</div>
      <div>
        <Label>Client *</Label>
        <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
          <SelectTrigger data-testid="site-profile-client-select"><SelectValue placeholder="Choose the client" /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {FIELDS.map(([k, label, req]) => (
          <div key={k}>
            <Label>{label}{req ? ' *' : ''}</Label>
            <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`site-profile-field-${k}`} />
            <p className="mt-0.5 text-[11px] text-slate-500">{proposal[k] && form[k] === proposal[k] ? `From file (${proposal.provenance[k]})` : form[k] ? 'Entered by you' : 'Not in the file'}</p>
          </div>
        ))}
      </div>
      <Button disabled={missing || busy} onClick={() => onCreate({ ...form, client_id: Number(form.client_id) })} data-testid="site-profile-create-btn">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create site and its locations
      </Button>
    </div>
  );
};

export const SiteProfilePanel = ({ stage0RunId, onSiteReady }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    historicalConversionService.getSiteProposal(stage0RunId).then(setData).catch((e) => setError(errText(e, 'Could not read the file')));
  }, [stage0RunId]);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(msg(r));
      onSiteReady(r.site_id);
    } catch (e) {
      toast.error(errText(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };
  if (error) return <Alert variant="destructive" data-testid="site-profile-error"><AlertDescription>{error}</AlertDescription></Alert>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="site-profile-loading"><Loader2 className="h-4 w-4 animate-spin" />Reading the file…</div>;
  const link = (id) => run(() => historicalConversionService.linkSiteToHouse(stage0RunId, id),
    (r) => `Linked house ${r.house} to ${r.name}${r.locations_added ? `; ${r.locations_added} location(s) added` : ''}`);
  return (
    <div className="space-y-4" data-testid="site-profile-panel">
      <SourceSummary s={data.summary} />
      {data.linked_site ? (
        <Alert data-testid="site-profile-linked"><AlertDescription className="flex flex-wrap items-center gap-2">
          House {data.summary.house} is already linked to <strong>{data.linked_site.name}</strong>.
          <Button size="sm" onClick={() => onSiteReady(data.linked_site.site_id)} data-testid="site-profile-use-linked-btn">Continue with this site</Button>
        </AlertDescription></Alert>
      ) : (
        <>
          {data.candidate_sites.length > 0 && (
            <div className="space-y-2 rounded-md border p-4" data-testid="site-profile-candidates">
              <div className="text-sm font-medium">Existing sites that look like this house</div>
              {data.candidate_sites.map((c) => (
                <div key={c.site_id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`site-profile-candidate-${c.site_id}`}>
                  <span>{c.name}{c.client_name ? ` · ${c.client_name}` : ''}{c.already_linked_to_a_house ? ' · linked to another house' : ''}</span>
                  <Button size="sm" variant="outline" disabled={busy || !data.writes_allowed || c.already_linked_to_a_house} onClick={() => link(c.site_id)} data-testid={`site-profile-link-btn-${c.site_id}`}>
                    <Link2 className="mr-1 h-4 w-4" />Use this site
                  </Button>
                </div>
              ))}
              <p className="text-xs text-slate-500">Linking adds the file's locations to the site if they are missing. Nothing else on the site changes.</p>
            </div>
          )}
          {data.writes_allowed
            ? <CreateSiteForm proposal={data.proposal} clients={data.clients} busy={busy}
                onCreate={(body) => run(() => historicalConversionService.createSiteFromSource({ ...body, stage0_run_id: stage0RunId }),
                  (r) => `Created ${r.name} with ${r.locations_added} location(s)`)} />
            : <p className="text-xs text-slate-500" data-testid="site-profile-readonly">Creating or linking sites is switched off in this environment.</p>}
        </>
      )}
    </div>
  );
};
